import React, { useEffect, useState } from 'react';
import CardSwiper from './components/card-swiper';
import useDetectIsMobile from '../../hook/use-detect-is-mobile';
import MapMarkerIcon from '../../assets/map-marker-icon';
import Button from '../../components/button';
import OriginDestinationItem from './components/origin-destination-item';
import FindTransport from './components/find-transport';
import { API, ROUTES } from '../../helpers/constants';
import { useNavigate } from 'react-router-dom';
import LabelAmount from '../../components/label-amount-icon';
import useGet from '../../hook/use-get';
import { SectionError } from '../../components/error';
import EmptyList from '../../components/empty-list';
import BannerContent from './components/banner';

export const TransportServices = () => {
  const {
    data: services,
    isFetching,
    isError,
    refetch
  } = useGet({
    queryKey: 'transportServices',
    api: `${API.transportServices}?group=1`
  });
  const { isTablet, isMobile } = useDetectIsMobile();

  const getNumberOfSlides = () => {
    if (isMobile) return 1;
    if (isTablet) return 2;
    return 3;
  };

  const renderContent = () => {
    if (isFetching) {
      return <div className="w-full h-40 bg-gray-300 animate-pulse" />;
    }

    if (isError) {
      return (
        <div className="h-40 items-center justify-center flex border border-b rounded-lg">
          <SectionError refetch={refetch} />
        </div>
      );
    }

    return <CardSwiper data={services} slidesPerView={getNumberOfSlides()} />;
  };

  return (
    <>
      <div className="flex flex-col md:flex-row justify-between items-center gap-5">
        <h2 className="flex-1 text-3xl text-center md:text-left font-semibold">
          Yuk Cari Tahu Layanan Transportasi
        </h2>
        <p className="flex-1 text-center md:text-left ">
          Kemudahan perjalanan kamu dengan berbagai layanan transportasi di Jawa Barat.
        </p>
      </div>
      <div className="m-auto my-12 lg:mb-0">{renderContent()}</div>
    </>
  );
};

const SearchTransport = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState('');
  const {
    data: popularCity,
    isFetching: isFetchingPopularCity,
    isError: isErrorPopularCity,
    refetchPopularCity
  } = useGet({
    queryKey: 'popularCity',
    api: API.popularCity,
    options: {
      onSuccess: (data) => {
        setSelected(data?.data?.[0]?.name)
      }
    }
  });
  const {
    data: popularDestination,
    isFetching: isFetchingPopularDestination,
    isError: isErrorPopularDestination,
    refetch: refetchPopularDest
  } = useGet({
    queryKey: 'popularDestination',
    api: `${API.popularDestination}/${selected}`,
    options: {
      enabled: !!selected
    }
  });

  useEffect(() => {
    if (selected) {
      refetchPopularDest();
    }
  }, [selected, refetchPopularDest]);

  const onSubmit = ({ origin, destination }) => {
    navigate(`${ROUTES.search_schedule}?asal=${origin}&tujuan=${destination}`);
  };

  const renderPopularDestinationContent = () => {
    if (isErrorPopularDestination) {
      return (
        <div className="h-40 items-center justify-center flex border border-b rounded-lg">
          <SectionError refetch={refetchPopularDest} />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-6 bg-white md:bg-transparent">
        {isFetchingPopularDestination ? (
          <div className="space-y-5 py-10">
            {Array(3)
              .fill(0)
              .map(() => (
                <div className="w-[70%] md:w-[50%] lg:w[70%] h-36 m-auto rounded-lg bg-gray-300 animate-pulse" />
              ))}
          </div>
        ) : (
          <div className="space-y-5 my-10">
            {!!popularDestination?.length ? (
              popularDestination?.map((item, idx) => (
                <div className="w-[70%] md:w-[50%] lg:w[70%] m-auto" key={item?.['operational_id']}>
                  <OriginDestinationItem
                    no={idx + 1}
                    origin={item?.['initial_city']}
                    destination={item?.['destination_city']}
                    cost={item?.tariff}
                    onNavigate={() =>
                      navigate(`${ROUTES.trans_schedule}/${item?.['operational_id']}/rute/${item?.rute}`)
                    }
                  />
                </div>
              ))
            ) : (
              <EmptyList />
            )}
          </div>
        )}
      </div>
    );
  };

  const renderPopularContent = () => {
    if (isErrorPopularCity) {
      return (
        <div className="h-40 items-center justify-center flex border border-b rounded-lg">
          <SectionError refetch={() => Promise.all([refetchPopularDest, refetchPopularCity])} />
        </div>
      );
    }

    return (
      <>
        <div className="shadow-md">
          <div className="flex flex-col md:flex-row justify-center items-center gap-12 pb-3 m-auto w-[80%]">
            <span className="inline-flex justify-center items-center gap-4">
              <MapMarkerIcon color="black" size={38} />
              <span className="text-2xl md:text-xl font-semibold text-left">Rute Populer</span>
            </span>
            {isFetchingPopularCity ? (
              <div className="w-1/2 h-20 bg-gray-300 animate-pulse" />
            ) : !!popularCity.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 mx-auto lg:grid-cols-4">
                {popularCity?.map((item) => (
                  <Button
                    key={item?.name}
                    text={item?.name}
                    isActive={selected === item?.name}
                    onClick={() => setSelected(item?.name)}
                  />
                ))}
              </div>
            ) : (
              <h1 className="font-semibold text-xl">Maaf, tidak ada data yang ditemukan</h1>
            )}
          </div>
        </div>
        {renderPopularDestinationContent()}
      </>
    );
  };

  return (
    <div className="bg-neutral-50">
      <div className="bg-white flex flex-col lg:flex-row lg:bg-transparent">
        <div className="lg:relative w-full space-y-5">
          <BannerContent />
          <div className="w-[90%] lg:w-[70%] z-30 lg:absolute m-auto lg:left-0 lg:right-0 lg:-translate-y-20">
            <FindTransport onSubmit={onSubmit} />
          </div>
        </div>
      </div>
      <div className="hidden lg:block h-36 bg-white" />
      <div className="bg-white py-8">
        <div className="w-[65%] m-auto">
          <LabelAmount />
        </div>
      </div>
      <div className="pt-10 md:pt-16 lg:pb-16 w-[90%] md:w-[80%] m-auto">
        <TransportServices />
      </div>
      <hr className="md:hidden my-4" />
      {renderPopularContent()}
    </div>
  );
};

export default SearchTransport;
